import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { GroupType } from '@chancenwerk/db/enums/enums.js';
import { ChancenPermission, ContextPrefix } from '@chancenwerk/db/permissions.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { of, switchMap, takeUntil } from 'rxjs';
import '../components/campus-component-student-selection.js';
import '../components/campus-delete-button.js';
import '../components/form-fields/campus-formfield-event-list.js';
import { CampusRoutes } from '../routes.js';

const userGroupSelectionSet: BaseQLSelectionSet<CampusUserGroup> = {
  name: 1,
  assignedLessonsIDs: 1,
  assignedEventIDs: 1,
  userIDs: 1,
};

/**
 * @customElement campus-page-administration-group-edit
 */
@customElement('campus-page-administration-group-edit')
export class CampusPageGroupEdit extends CrPage {
  @property() groupID = new RXController(this, '');

  @state() private readonly _groupTypeParam = new RXController(this, routingState.searchParams.get('groupType'));

  @property({ attribute: false }) public groupType: RXController<GroupType> = new RXController<GroupType>(
    this,
    GroupType.STUDENTS,
  );
  @state() public userGroup = new RXController(this, this.groupID.observable, $groupID =>
    $groupID.pipe(
      switchMap(id => {
        if (!id) {
          return of(new CampusUserGroup({}));
        } else {
          return CampusUserGroup.subscribeByID<CampusUserGroup>(id)(userGroupSelectionSet);
        }
      }),
    ),
  );

  @state() private readonly _errorMsg = new RXController(this, '');
  @state() private readonly _selectedTab = new RXController(this, 0);
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  private readonly _form = new FormController(this, this.userGroup.observable, {
    success: async studentGroup => this._handleFormSuccess(studentGroup),
    error: err => this._handleFormError(err),
  });

  constructor() {
    super();
    this.label = 'Gruppe erstellen';
    this.description = 'Erstelle eine neue Gruppe';
    this.permission = ChancenPermission.campus.EDIT_GROUPS;
    this.stickyHeader = true;
    this.backRoute = CampusRoutes.groups;
    this.noBottomPadding = true;
    this.fixedHeight = true;
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this.userGroup.observable.pipe(takeUntil(this.disconnected)).subscribe(userGroup => {
      if (userGroup?._id) {
        this.label = 'Gruppe bearbeiten';
        this.description = 'Bearbeite eine Gruppe';
      } else {
        this.label = 'Gruppe erstellen';
        this.description = 'Erstelle eine neue Gruppe';
      }
    });

    this._groupTypeParam.observable.pipe(takeUntil(this.disconnected)).subscribe(groupType => {
      if (!groupType) {
        return;
      }

      this.groupType.next(parseInt(groupType) as GroupType);
    });
  }

  private _newEventIDs: string[] = [];

  private async _handleFormSuccess(userGroup: CampusUserGroup) {
    // succes form create for new object or save on existing object
    await ChromiumDialog.guard(
      async () => {
        if (!userGroup._id) {
          userGroup.groupType = this.groupType.value;

          // Only connect the kpf if the user has no global permission for this feature
          if (!this._currentUser.value?.hasPermission(ChancenPermission.campus.EDIT_GROUPS, GLOBAL_CONTEXT)) {
            for (const role of this._currentUser.value?.roles.filter(r =>
              r.permissions.includes(ChancenPermission.campus.EDIT_GROUPS),
            ) ?? []) {
              if (!role.contexts.includes(GLOBAL_CONTEXT)) {
                // TODO should we allways take the first kpf with the correct permission. In 99% of the cases the user only has 1 kpf associated
                userGroup.kpfID = role.contexts[0]?.replace(ContextPrefix.KPF, '');
                break;
              }
            }
          }

          await userGroup.create();
        } else {
          await userGroup.save();
        }
      },
      {
        success: () => ChromiumSnackbar.show('Erfolgreich gespeichert!'),
      },
    );
  }

  private _handleFormError(err: ValidationError<any>) {
    this._errorMsg.next(err.message);
  }

  override content() {
    return html`
      <cr-flex space="sm" padding="xs" horizontal ${css({ minHeight: 0 })}>
        <cr-flex vertical space="sm" ${css({ flex: 1 })}>
          <cr-card ${css({ flexShrink: 0 })}>
            <chancen-component-card-title>Basis-Informationen</chancen-component-card-title>
            <cr-input
              ${css({ marginTop: this.spacing.xs })}
              ${this._form.field('name')}
              placeholder="Gruppenname*"
            ></cr-input>
          </cr-card>
          <cr-card ${css({ minHeight: 0 })}>
            <campus-component-student-selection
              flex
              .userType=${this.groupType.value}
              .checkedStudentIDs=${this.userGroup.value?.userIDs}
              @value-picked=${(e: CustomEvent<{ value: ChancenwerkUser }>) => {
                // Remove Clicked Student
                if (this.userGroup.value?.userIDs?.includes(e.detail.value._id)) {
                  const indexOfStudentID = this.userGroup.value.userIDs.findIndex(
                    studentID => studentID === e.detail.value._id,
                  );
                  this.userGroup.value?.userIDs.splice(indexOfStudentID, 1);
                } else {
                  // Add Clicked Student
                  this.userGroup.value?.userIDs?.push(e.detail.value._id);
                }
                // reactivity breaks after the first update of the studentGroup.userIDs array
                this.requestUpdate();
              }}
            ></campus-component-student-selection>
          </cr-card>
        </cr-flex>
        <cr-card ${css({ flex: 3, minHeight: 0 })} space="md">
          <cr-tabs .selected=${this._selectedTab.value} ${css({ overflowX: 'hidden', height: '63px' })}>
            <cr-tab @click=${() => requestAnimationFrame(() => this._selectedTab.next(0))}> Lektion </cr-tab>
            <cr-tab @click=${() => requestAnimationFrame(() => this._selectedTab.next(1))}> Events </cr-tab>
          </cr-tabs>
          ${this._showSelectionContent()}
        </cr-card>
      </cr-flex>
    `;
  }

  override header() {
    return html` <cr-flex horizontal end crossaxis-center space="md">
      ${this.userGroup.value?._id
        ? html`
            <campus-delete-button
              @click=${async () => {
                if (!(await ChromiumDialog.confirm('Gruppe wirklich löschen?'))) return;
                await this.userGroup.value?.remove();
                void ChromiumSnackbar.show('Gruppe wurde gelöscht');
                goTo(CampusRoutes.groups);
              }}
            >
            </campus-delete-button>
          `
        : nothing}

      <cr-flex horizontal space="sm" crossaxis-center>
        <cr-button @click=${() => this._form.validate()}>
          ${!this.userGroup.value?._id ? html` Erstellen ` : html` Speichern `}
        </cr-button>
      </cr-flex>
    </cr-flex>`;
  }

  private _showSelectionContent() {
    switch (this._selectedTab.value) {
      case 0:
        return this._showLessonSelection();
      case 1:
        return this._showEventSelection();
      default:
        break;
    }
  }

  private _showLessonSelection() {
    return html`
      <campus-component-section-lesson-list
        flex
        ${css({ overflowY: 'auto', overflowX: 'hidden' })}
        .area=${this.groupType.value === GroupType.STUDENTS ? CampusArea.STUDENTS : CampusArea.EMPLOYEES}
        .groupType=${this.groupType.value}
        .markedLessonIDs=${this.userGroup.value?.assignedLessonsIDs ?? []}
        .mode=${'custom'}
        .userHiddenFromStudents=${false}
        custom-title="Lektionen auswählen"
        dark-cards
        @lessonClicked=${async (e: CustomEvent) => {
          if (!e.detail.lesson) return;
          const lessonClicked = e.detail.lesson as CampusLesson;
          // Remove Clicked Lesson
          if (this.userGroup.value?.assignedLessonsIDs?.includes(lessonClicked._id)) {
            const indexOfLessonID = this.userGroup.value.assignedLessonsIDs.findIndex(
              lessonID => lessonID === lessonClicked._id,
            );
            if (indexOfLessonID === -1)
              return console.error(
                'Tried to remove lesson from assignedLessons, but didnt found the _id in the array.',
              );
            this.userGroup.value.assignedLessonsIDs.splice(indexOfLessonID, 1);
          } else {
            // Add Clicked Lesson
            this.userGroup.value?.assignedLessonsIDs?.push(lessonClicked._id);
          }
          // reactivity breaks after the first update of the studentGroup.assignedLessonsIDs array
          this.requestUpdate();
        }}
      ></campus-component-section-lesson-list>
    `;
  }

  private _showEventSelection() {
    return html`
      <campus-formfield-event-list
        ${css({ overflowY: 'auto' })}
        flex
        .area=${this.groupType.value === 0 ? CampusArea.STUDENTS : CampusArea.EMPLOYEES}
        ${this._form.field('assignedEventIDs')}
        @value-added=${(event: ValueEvent<string[]>) => {
          this._newEventIDs = this._newEventIDs.concat(event.detail.value);
        }}
      ></campus-formfield-event-list>
    `;
  }
}
